import React, { useCallback, useEffect, useMemo } from "react";
import { CloseIcon } from "@icons";
import { useEntities } from "@hooks/useEntities";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useConsolidationRelationships } from "@api/consolidation.api";
import { ConsolidationEligibilityBody } from "@common/schemas/domains/accounting/consolidation.schema";
import { WorkspaceMember } from "@common/types/workspace";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Drawer } from "@design-system/Drawer/Drawer";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { useOverlays } from "contexts/OverlayContext";
import { ConsolidationSelectEntitiesStep } from "./ConsolidationSelectEntitiesStep";
import { ConsolidationConfirmStep } from "./ConsolidationConfirmStep";
import {
    ConsolidationStep,
    useConsolidationSetupState,
} from "./useConsolidationSetupState";
import { useConsolidationSubmission } from "./useConsolidationSubmission";
import styles from "./ConsolidationWorkflow.module.scss";

const WORKFLOW_TITLE = "Add consolidation";

const STEP_META: Record<ConsolidationStep, { action: string }> = {
    [ConsolidationStep.SelectEntities]: {
        action: "Select parent and subsidiary entities",
    },
    [ConsolidationStep.ConfirmGroup]: {
        action: "Confirm ledger basis to consolidate",
    },
};

interface ConsolidationWorkflowProps {
    open: boolean;
    onClose: () => void;
    initialSelection?: ConsolidationEligibilityBody;
}

export const ConsolidationWorkflow: React.FC<ConsolidationWorkflowProps> = ({
    open,
    onClose,
    initialSelection,
}) => {
    const workspace = useActiveWorkspace();
    const workspaceId = workspace?.id ?? "";
    const entities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const membersQuery = useCurrentWorkspaceMembers();
    const relationshipsQuery = useConsolidationRelationships(
        workspaceId || null,
    );
    const relationships = useMemo(
        () => relationshipsQuery.data ?? [],
        [relationshipsQuery.data],
    );
    const {
        step,
        selectedEntityUuids,
        parentEntityUuid,
        subsidiaryEntityUuids,
        ledgerOptionsByEntityUuid,
        ledgerIdsByEntityUuid,
        isLoadingBasisOptions,
        basisOptionsError,
        isDirectConfirmation,
        toggleEntity,
        toggleAll,
        setRole,
        setSelectedLedger,
        loadBasisOptions,
        goBack: goBackToEntitySelection,
        resetSetup,
    } = useConsolidationSetupState(
        workspaceId,
        relationships,
        initialSelection,
    );

    const membersByEntityId = useMemo(() => {
        const map = new Map<number, WorkspaceMember[]>();
        for (const member of membersQuery.data ?? []) {
            for (const assignment of member.entityAssignments) {
                const list = map.get(assignment.entity.id) ?? [];
                list.push(member);
                map.set(assignment.entity.id, list);
            }
        }
        return map;
    }, [membersQuery.data]);

    const parentEntity = useMemo(
        () => entities.find((entity) => entity.uuid === parentEntityUuid),
        [entities, parentEntityUuid],
    );
    const subsidiaryEntities = useMemo(
        () =>
            subsidiaryEntityUuids
                .map((uuid) => entities.find((entity) => entity.uuid === uuid))
                .filter((entity): entity is (typeof entities)[number] =>
                    Boolean(entity),
                ),
        [entities, subsidiaryEntityUuids],
    );

    const handleSubmissionSuccess = useCallback(() => {
        resetSetup();
        onClose();
    }, [onClose, resetSetup]);
    const { isSubmitting, submissionError, submit, resetSubmission } =
        useConsolidationSubmission({
            workspaceId,
            parentEntity,
            entities,
            subsidiaryEntityUuids,
            relationships,
            ledgerIdsByEntityUuid,
            onSuccess: handleSubmissionSuccess,
        });

    const resetWorkflow = useCallback(() => {
        resetSetup();
        resetSubmission();
    }, [resetSetup, resetSubmission]);
    const handleClose = useCallback(() => {
        resetWorkflow();
        onClose();
    }, [onClose, resetWorkflow]);
    const goBack = useCallback(() => {
        if (isDirectConfirmation) {
            handleClose();
            return;
        }
        goBackToEntitySelection();
        resetSubmission();
    }, [
        goBackToEntitySelection,
        handleClose,
        isDirectConfirmation,
        resetSubmission,
    ]);

    useEffect(() => {
        if (!open) {
            resetWorkflow();
        }
    }, [open, resetWorkflow]);

    const isFirstStep = step === ConsolidationStep.SelectEntities;
    const isLastStep = step === ConsolidationStep.ConfirmGroup;
    const meta = STEP_META[step];

    const canContinueFromSelect =
        parentEntityUuid !== null &&
        subsidiaryEntityUuids.length >= 1 &&
        relationshipsQuery.isSuccess;
    const isPrimaryDisabled =
        (step === ConsolidationStep.SelectEntities && !canContinueFromSelect) ||
        (step === ConsolidationStep.ConfirmGroup &&
            (isLoadingBasisOptions ||
                basisOptionsError !== null ||
                Object.keys(ledgerOptionsByEntityUuid).length === 0 ||
                Object.entries(ledgerOptionsByEntityUuid).some(
                    ([entityUuid, ledgerOptions]) =>
                        ledgerOptions.length === 0 ||
                        ledgerIdsByEntityUuid[entityUuid] === undefined,
                )));

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            size="fullScreen"
            noPadding
            renderHeader={false}
            dismissible={!isSubmitting}
        >
            <div className={styles.workflow}>
                <header className={styles.header}>
                    <div className={styles.titleRow}>
                        <h1 className={styles.title}>{WORKFLOW_TITLE}</h1>
                        <Button
                            variant="secondary"
                            iconOnly
                            size="sm"
                            aria-label="Close consolidation workflow"
                            className={styles.closeButton}
                            disabled={isSubmitting}
                            onClick={handleClose}
                        >
                            <CloseIcon />
                        </Button>
                    </div>
                    {!isDirectConfirmation && (
                        <div className={styles.stepRow}>
                            <div className={styles.stepIndicator}>
                                <span>Step {isFirstStep ? 1 : 2} of 2</span>
                                <span
                                    className={styles.stepSeparator}
                                    aria-hidden
                                >
                                    •
                                </span>
                                <span>{meta.action}</span>
                            </div>
                        </div>
                    )}
                </header>
                <div className={styles.content}>
                    {step === ConsolidationStep.SelectEntities && (
                        <ConsolidationSelectEntitiesStep
                            entities={entities}
                            selectedEntityUuids={selectedEntityUuids}
                            parentEntityUuid={parentEntityUuid}
                            membersByEntityId={membersByEntityId}
                            onToggleEntity={toggleEntity}
                            onToggleAll={toggleAll}
                            onSetRole={setRole}
                        />
                    )}
                    {step === ConsolidationStep.ConfirmGroup &&
                        parentEntity && (
                            <ConsolidationConfirmStep
                                parentEntity={parentEntity}
                                subsidiaryEntities={subsidiaryEntities}
                                membersByEntityId={membersByEntityId}
                                ledgerOptionsByEntityUuid={
                                    ledgerOptionsByEntityUuid
                                }
                                ledgerIdsByEntityUuid={ledgerIdsByEntityUuid}
                                isLoadingBasisOptions={isLoadingBasisOptions}
                                basisOptionsError={basisOptionsError}
                                onLedgerChange={setSelectedLedger}
                                onRetryBasisOptions={loadBasisOptions}
                                onBack={goBack}
                            />
                        )}
                </div>
                <div className={styles.footerBar}>
                    <CommandIsland className={styles.footer}>
                        <CommandIslandTotal>
                            {selectedEntityUuids.length} selected
                        </CommandIslandTotal>
                        <div className={styles.footerRight}>
                            {submissionError && (
                                <span className={styles.error}>
                                    {submissionError}
                                </span>
                            )}
                            <div className={styles.footerActions}>
                                {isLastStep && (
                                    <ButtonWithLoader
                                        variant="secondary"
                                        size="md"
                                        type="button"
                                        loading={false}
                                        disabled={isSubmitting}
                                        onClick={goBack}
                                    >
                                        {isDirectConfirmation
                                            ? "Cancel"
                                            : "Back"}
                                    </ButtonWithLoader>
                                )}
                                <WithHotkey
                                    hotKey="mod+enter"
                                    hotKeyDisplayPosition="inline"
                                    hotKeyOptions={{
                                        description: isLastStep
                                            ? "Consolidate"
                                            : "Continue",
                                        enabled:
                                            open &&
                                            !isPrimaryDisabled &&
                                            !isSubmitting,
                                    }}
                                >
                                    {(hotkeyHint) => (
                                        <Button
                                            variant="primary"
                                            size="md"
                                            disabled={
                                                isPrimaryDisabled ||
                                                isSubmitting
                                            }
                                            onClick={
                                                isLastStep
                                                    ? submit
                                                    : loadBasisOptions
                                            }
                                            rightIcon={hotkeyHint}
                                        >
                                            {isLastStep
                                                ? "Consolidate"
                                                : "Continue"}
                                        </Button>
                                    )}
                                </WithHotkey>
                            </div>
                        </div>
                    </CommandIsland>
                </div>
            </div>
        </Drawer>
    );
};

export const ConsolidationWorkflowContainer: React.FC = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo("consolidationWorkflow");

    if (!overlayInfo) {
        return null;
    }

    return (
        <ConsolidationWorkflow
            key={overlayInfo.instanceId}
            open={!overlayIsClosing}
            onClose={closeOverlay}
            initialSelection={overlayInfo.data?.initialSelection}
        />
    );
};
